import { useState } from 'react';
import { api, team as Api, type Role, type TeamMember } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { Shim, SkeletonRows } from '../components/Shimmer';
import { useSelection, TickAll, Tick, BulkActions } from '../components/Bulk';
import { Modal } from '../components/Modal';
import { Field, Row } from '../components/Field';

const when = (v: string) => {
  const d = new Date(String(v).replace(' ', 'T') + 'Z');
  return isNaN(d.valueOf()) ? v
    : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

export function Team() {
  const { data, error, loading, reload } = useAsync((signal) => Api.list(signal));
  const [editing, setEditing] = useState<TeamMember | 'new' | null>(null);
  const members = data?.team ?? [];

  async function viewAs(m: TeamMember) {
    if (!confirm(`See the console as ${m.name || m.email}? Everything you do is recorded against your own account.`)) return;
    try {
      await api.post(`/team/${m.id}/view-as`);
      location.href = '/v2/dashboard';
    } catch (e) { alert((e as Error).message); }
  }

  async function remove(m: TeamMember) {
    if (!confirm(`Remove ${m.email}? They lose access immediately.`)) return;
    try { await Api.remove(m.id); await reload(); }
    catch (e) { alert((e as Error).message); }
  }

  const sel = useSelection(members);

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>Team</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7 }}>
            {loading && !data ? <Shim w={210} h={12} style={{ marginTop: 3 }} />
              : `${members.length} account${members.length === 1 ? '' : 's'} · ${data?.owner_count ?? 0} owner${data?.owner_count === 1 ? '' : 's'}`}
          </p>
        </div>
        <button className="b bg" type="button" onClick={() => setEditing('new')}>Add someone</button>
      </div>

      {error ? <div className="err">Could not load the team — {error.message}</div> : null}

      {data && data.owner_count === 1 ? (
        <div className="crd" style={{ padding: '14px 18px', marginBottom: 16, borderColor: 'rgba(var(--goldrgb), .28)' }}>
          <p style={{ fontSize: 13, color: 'var(--tx2)', margin: 0 }}>
            There is one Owner. If that account is lost, nobody can grant access back without shell access
            to the server — a second Owner is the cheapest insurance there is.
          </p>
        </div>
      ) : null}

      <div className="crd" style={{ padding: '20px 22px' }}>
        <BulkActions
          path="/team" noun="team member"
          rows={sel.picked.map((m) => ({ id: m.id, label: String(m.email) }))}
          actions={[['activate', 'Activate'], ['suspend', 'Suspend']]}
          onDone={async () => { sel.clear(); await reload(); }}
        />

        <div className="tblx">
          <table className="tbl">
            <thead>
              <tr>
                <TickAll on={sel.allOn} some={sel.picked.length > 0} onChange={sel.toggleAll} /><th>Name</th><th>Email</th><th>Role</th><th>State</th><th /></tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <SkeletonRows cols={6} tick />
              ) : members.map((m) => (
                <tr key={m.id} style={sel.isOn(m.id) ? { background: 'rgba(var(--goldrgb), .05)' } : undefined}>
                  <Tick on={sel.isOn(m.id)} label={String(m.email)}
                    onChange={(v) => sel.toggle(m.id, v)} />
                  <td>
                    <div style={{ fontWeight: 700 }}>
                      {m.name || '—'}
                      {m.id === data?.me ? <span style={{ fontSize: 11, color: 'var(--tx3)', marginLeft: 8 }}>you</span> : null}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--tx3)' }}>added {when(m.created_at)}</div>
                  </td>
                  <td className="muted">{m.email}</td>
                  <td>
                    {m.role || '—'}
                    {m.is_owner ? <span className="tg" style={{ marginLeft: 8, background: 'var(--goldsoft)',
                      color: 'var(--goldtx)' }}>owner</span> : null}
                  </td>
                  <td>
                    <span className="tg" style={{
                      background: m.state === 'active' ? 'rgba(62,207,142,.14)' : 'rgba(240,100,91,.13)',
                    }}>{m.state}</span>
                  </td>
                  <td className="right" style={{ whiteSpace: 'nowrap' }}>
                    {m.id !== data?.me && m.state === 'active' ? (
                      <button className="b bo" style={{ padding: '6px 10px', fontSize: 11.5 }}
                        type="button" onClick={() => void viewAs(m)}>View as</button>
                    ) : null}
                    <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5, marginLeft: 6 }}
                      type="button" onClick={() => setEditing(m)}>Edit</button>
                    {m.id !== data?.me ? (
                      <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5, marginLeft: 6 }}
                        type="button" onClick={() => void remove(m)}>Remove</button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="crd" style={{ padding: '20px 22px', marginTop: 18 }}>
        <div className="kh" style={{ marginBottom: 6 }}>Recent activity</div>
        <p className="hint" style={{ marginBottom: 12 }}>
          Role changes, account switches and password reads, so a change can always be traced to a person.
        </p>
        {(data?.audit ?? []).length === 0 ? (
          <p className="muted" style={{ fontSize: 13 }}>Nothing recorded yet.</p>
        ) : (
          <div className="tblx">
            <table className="tbl">
              <thead><tr><th>When</th><th>Who</th><th>What</th><th>Detail</th></tr></thead>
              <tbody>
                {(data?.audit ?? []).map((a) => (
                  <tr key={a.id}>
                    <td className="muted" style={{ whiteSpace: 'nowrap' }}>{when(a.created_at)}</td>
                    <td className="muted">{a.actor_email || '—'}</td>
                    <td>{a.action}</td>
                    <td className="muted">{a.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing ? (
        <TeamForm
          member={editing === 'new' ? null : editing}
          roles={data?.roles ?? []}
          onCancel={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await reload(); }}
        />
      ) : null}
    </div>
  );
}

function TeamForm({ member, roles, onCancel, onSaved }: {
  member: TeamMember | null;
  roles: Role[];
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState(member?.name ?? '');
  const [email, setEmail] = useState(member?.email ?? '');
  const [roleId, setRoleId] = useState<number>(member?.role_id ?? roles[0]?.id ?? 0);
  const [state, setState] = useState(member?.state ?? 'active');
  const [password, setPassword] = useState('');
  const [shown, setShown] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  async function save() {
    if (!email.trim()) { setError('An email is required.'); return; }
    if (!member && password.length < 10) {
      setError('A new account needs a password of at least 10 characters.');
      return;
    }
    if (member && password && password.length < 10) {
      setError('Use at least 10 characters, or leave it blank to keep the current password.');
      return;
    }
    setError('');
    setStatus('Saving…');
    try {
      if (member) {
        await Api.update(member.id, {
          name, email, role_id: roleId, state,
          ...(password ? { password } : {}),
        });
      } else {
        await Api.create({ name, email, password, role_id: roleId });
      }
      await onSaved();
    } catch (e) { setStatus(''); setError((e as Error).message); }
  }

  return (
    <Modal
      title={member ? `Edit · ${member.name || member.email}` : 'Add someone to the team'}
      onClose={onCancel}
      footer={
        <>
          <span style={{ fontSize: 12, color: 'var(--tx3)' }}>{status}</span>
          <span className="mdl-spacer" />
          <button className="b bo" type="button" onClick={onCancel}>Cancel</button>
          <button className="b bg" type="button" onClick={() => void save()}>Save</button>
        </>
      }
    >
      {error ? <div className="err">{error}</div> : null}

      <Row>
        <Field label="Name"><input className="inp" value={name} autoFocus
          onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Email"><input className="inp" type="email" value={email}
          onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label="Role">
          <select className="inp" value={roleId} onChange={(e) => setRoleId(Number(e.target.value))}>
            {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </Field>
      </Row>

      {member ? (
        <Field label="Account state" hint="A disabled account keeps its history but cannot sign in.">
          <select className="inp" value={state} onChange={(e) => setState(e.target.value as 'active' | 'disabled')}>
            <option value="active">active</option>
            <option value="disabled">disabled</option>
          </select>
        </Field>
      ) : null}

      <Field
        label={member ? 'New password' : 'Password'}
        hint={member
          ? 'Leave blank to keep the current one. Setting a new one signs this person out everywhere.'
          : 'At least 10 characters. Console accounts are held to a longer password than members.'}
      >
        <div style={{ display: 'flex', gap: 8 }}>
          <input className="inp" type={shown ? 'text' : 'password'} autoComplete="new-password"
            value={password} onChange={(e) => setPassword(e.target.value)} />
          <button className="b bo" type="button" style={{ padding: '9px 14px', fontSize: 12 }}
            onClick={() => setShown((v) => !v)}>{shown ? 'Hide' : 'Show'}</button>
          <button className="b bo" type="button" style={{ padding: '9px 14px', fontSize: 12 }}
            onClick={() => {
              const abc = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
              const n = new Uint32Array(14);
              crypto.getRandomValues(n);
              setPassword([...n].map((v) => abc[v % abc.length]).join(''));
              setShown(true);
            }}>Generate</button>
        </div>
      </Field>
    </Modal>
  );
}
